import React, { useState, useEffect } from 'react';
import { Button, Col, Input, Layout, Modal, Row, Upload, message} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import queryString from 'query-string';

import PdButton from 'components/pd-button';
import { TableList } from 'components/table-list';
import MediaService from 'services/media';
import UploadService from 'services/upload';
import { errHandling, copyTextToClipboard } from 'utils';

import './styles.scss';
import { useNavigate, useLocation } from 'react-router-dom';

const { Search } = Input;
const { Content } = Layout;
const { Dragger } = Upload;

export function Media() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [fetchingMedia, setFetchingMedia] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [mediaList, setMediaList] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [nextPage, setNextPage] = useState(-1);
  const [uploading, setUploading] = useState(false);

  const parsed = queryString.parse(location.search);

  function closeModal() {
    setModalVisible(false);
  }

  function showModal() {
    setModalVisible(true);
  }

  async function getListMedia() {
    setFetchingMedia(true);
    try {
      const { data: { data = [], metadata = {} } = {} } =
        await MediaService.getListMedia(parsed);
      setMediaList(data);
      setNextPage(metadata.next_cursor);
    } catch (error) {
      errHandling(error);
    } finally {
      setFetchingMedia(false);
    }
  }

  async function createMedia() {
    setUploading(true);
    try {
      if (fileList.length === 1) {
        const [payload] = fileList;
        await MediaService.createMedia(payload);
      } else if (fileList.length > 1) {
        await Promise.all(
          fileList.map((payload) => MediaService.createMedia(payload))
        );
      }
      setFileList([]);
      setModalVisible(false);
      getListMedia();
    } catch (error) {
      errHandling(error);
    } finally {
      setUploading(false);
    }
  }

  async function deleteMedia(id) {
    Modal.confirm({
      title: 'Are you sure?',
      content: 'Are you sure you want to delete this media?',
      onOk: async () => {
        try {
          await MediaService.deleteMedia(id);
          getListMedia();
        } catch (error) {
          errHandling(error);
        }
      },
      onCancel: () => {},
    });
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['cursor'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: location.pathname,
      search: stringified,
    });
  }

  const deleteButton = (row) => (
    <Button danger variant='outlined' onClick={() => deleteMedia(row.id)}>
      Delete
    </Button>
  );
  const mediaUrl = (row) => (
      <Button onClick={() =>
        copyTextToClipboard(row.media_url).then(() =>
          message.success(row.media_url + ' copied')
        )
      }>
      <img
        alt="copy-clipboard"
        src={`/img/icons/clipboard.svg`}
      />
      <p className="url">Copy URL</p>
      </Button>
  );

  const mediaImg = (row) => (
    <div className="media-container">
      <img className="media" src={row.media_url} alt="media" />
      {/* <p className="url">{row.title}</p>*/}
    </div>
  );


  const columns = [
    {
      name: 'Upload Date',
      selector: row => row.created_at,
      center: true,
      format: (row) => dayjs(row.created_at).format('DD/MM/YYYY HH:mm'),
      width: '10%',
    },
    {
      name: 'Media',
      center: true,
      selector: row => row.media_url,
      cell: mediaImg,
      width: '20%',
    },
    {
      name: 'Title',
      selector: row => row.title,
      width: '20%',
    },
    {
      name: 'URL',
      selector: row => row.media_url,
      center: true,
      cell: mediaUrl,
      width: '20%',
    },
    {
      width: '10%',
      right:true,
      cell: deleteButton,
    },
  ];

  const uploaderProps = {
    name: 'file',
    multiple: true,
    accept: 'image/*',
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        setUploading(true);
        const [type, ext] = file.type.split('/');
        const {
          data: { data: uploadURL },
        } = await UploadService.getUploadURL(ext, 'collection');
        await UploadService.uploadImage(file, uploadURL.upload_url);
        setFileList((prevFileList) => [
          ...prevFileList,
          {
            media: {
              media_type: type,
              media_url: uploadURL.secure_url,
              thumbnail: uploadURL.secure_url,
              title: file.name,
            },
          },
        ]);
        onSuccess();
      } catch (err) {
        errHandling(err);
        onError();
      } finally {
        setUploading(false);
      }
    },
  };

  function handleOnSearch(value) {
    parsed.q = value;
    navigate({
      pathname: location.pathname,
      search: queryString.stringify(parsed),
    });
  }

  function handleFilter(type) {
    parsed.media_type = type;
    navigate({
      pathname: location.pathname,
      search: queryString.stringify(parsed),
    });
  }

  useEffect(() => {
    getListMedia();
    setCurrentPage(parsed.cursor);
    // eslint-disable-next-line
  }, [parsed.q, parsed.media_type, parsed.cursor, parsed.limit]);

  return (
    <>
      <Modal
        destroyOnClose
        footer={null}
        onCancel={closeModal}
        onOk={closeModal}
        title="Upload Media"
        open={modalVisible}>
        <Dragger {...uploaderProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag a picture here to upload
          </p>
        </Dragger>
        <Row justify="center" type="flex">
          <Col span={12}>
            <PdButton
              className="pd-margin-top-md"
              disabled={fileList.length === 0}
              full
              loading={uploading}
              onClick={createMedia}
              type="primary">
              Create
            </PdButton>
          </Col>
        </Row>
      </Modal>
      <Layout className="main-container">
        <div className="content">
          <div className="pageTitle">Media</div>
          <Content className="pageContent page-content-media">
            <Row gutter={16} justify="space-between">
              <Col span={8}>
                <Search
                  allowClear
                  placeholder="Search by title"
                  size="large"
                  onSearch={handleOnSearch}
                />
              </Col>
              <Col span={4} offset={12}>
                <PdButton full onClick={showModal} type="primary">
                  Upload New Media
                </PdButton>
              </Col>
              <Col span={24}>
                <div className="filter">
                  <h3>Filter Media Type</h3>
                  <Button size="large" onClick={handleFilter}>
                    All
                  </Button>
                  <Button size="large" onClick={handleFilter}>
                    Images
                  </Button>
                  <Button size="large" onClick={() => handleFilter('video')}>
                    Video
                  </Button>
                </div>
              </Col>
              {!fetchingMedia && (
<TableList
                    changePage={changePage}
                    columns={columns}
                    loadingOrder={fetchingMedia}
                    nextPage={nextPage}
                    orderList={mediaList}
                    page={currentPage}
                    responsive={true}
                  />
              )}
            </Row>
          </Content>
        </div>
      </Layout>
    </>
  );
}
